import FilterChip from 'LuxKit/Chips/FilterChip'
import DropdownChevron from 'LuxKit/Dropdown/DropdownChevron'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import RadioInputListItem from 'LuxKit/List/RadioInputListItem'
import FormModal from 'LuxKit/Modal/FormModal'
import ModalContext from 'contexts/ModalContext'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import useToggle from 'hooks/useToggle'
import { capitalise } from 'lib/string/stringUtils'
import React, { useCallback, useContext, useRef } from 'react'
import { useIntl } from 'react-intl'

export const getMerchandiseSearchSortOptions = (intl: ReturnType<typeof useIntl>): Array<{ label: string, value: App.MerchandiseSearchSort }> => [
  {
    label: intl.formatMessage({
      id: 'BXedCmKZh3',
      defaultMessage: 'Recommended',
      description: 'Sort option label for recommended sorting',
    }),
    value: 'recommended',
  },
  {
    label: intl.formatMessage({
      id: 'ovCSOV1BBP',
      defaultMessage: 'New Arrivals',
      description: 'Sort option label for new arrivals sorting',
    }),
    value: 'newArrivals',
  },
  {
    label: intl.formatMessage({
      id: 'dLNQ+5E+bN',
      defaultMessage: 'Price (lowest)',
      description: 'Sort option label for lowest price sorting',
    }),
    value: 'priceAsc',
  },
  {
    label: intl.formatMessage({
      id: 'UP49TmDFmi',
      defaultMessage: 'Price (highest)',
      description: 'Sort option label for highest price sorting',
    }),
    value: 'priceDesc',
  },
]

interface Props {
  /**
   * The current sort by value
   *
   * @default recommended
   */
  sortBy?: App.MerchandiseSearchSort
  onChange?: (sortBy: App.MerchandiseSearchSort) => void
}

function MerchandiseSearchPageSort(props: Props) {
  const { sortBy = 'recommended', onChange } = props
  const intl = useIntl()
  const [openDropdown, toggleDropdown, _, closeDropdown] = useToggle()

  const anchorRef = useRef<HTMLButtonElement>(null)
  const showModal = useContext(ModalContext)
  const isMobile = useScreenSizeOnly('mobile')

  const sortOptions = getMerchandiseSearchSortOptions(intl)

  const sortByModalTitle = intl.formatMessage({
    id: 'Llf+mBGpVG',
    defaultMessage: 'Sort by',
    description: 'Title for sort by modal',
  })

  const sortByLabel = intl.formatMessage({
    id: 'ZW6MyaC0dU',
    defaultMessage: 'Sort',
    description: 'Label prefix for sort filter chip',
  })

  const onFilterClick = useCallback(async() => {
    if (isMobile) {
      const result = await showModal<{ sortBy: App.MerchandiseSearchSort }>(resolve => <FormModal title={sortByModalTitle} flush>
        {sortOptions.map((option) => <RadioInputListItem
          key={option.value}
          name="sortBy"
          title={option.label}
          value={option.value}
          defaultChecked={sortBy === option.value}
          onChange={e => resolve({ sortBy: e.target.value as App.MerchandiseSearchSort })}
          inputPlacement="start"
        />)}
      </FormModal>)

      if (result?.sortBy) {
        onChange?.(result.sortBy)
      }
    } else {
      toggleDropdown()
    }
  }, [isMobile, onChange, showModal, sortBy, toggleDropdown, sortByModalTitle, sortOptions])

  const onSortChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    onChange?.(e.target.value as App.MerchandiseSearchSort)
    closeDropdown()
  }, [onChange, closeDropdown])

  const selectedSortLabel = sortOptions.find(option => option.value === sortBy)?.label ?? ''

  return <>
    <FilterChip
      size="medium"
      ref={anchorRef}
      onClick={onFilterClick}
      endIcon={<DropdownChevron open={openDropdown} />}
    >
      {sortByLabel}: {capitalise(selectedSortLabel)}
    </FilterChip>
    <DropdownList
      open={openDropdown}
      anchorRef={anchorRef}
      triggerRef={anchorRef}
      size="S"
      onClose={closeDropdown}
    >
      {sortOptions.map((option) => <RadioInputListItem
        key={option.value}
        name="sortBy"
        title={option.label}
        value={option.value}
        defaultChecked={sortBy === option.value}
        inputPlacement="start"
        onChange={onSortChange}
      />)}
    </DropdownList>
  </>
}

export default MerchandiseSearchPageSort
